/**
 * UserActions — the ONE shared Follow + Message affordance dropped in wherever
 * another user's avatar/name is shown (search results, profile card, wheel-view
 * owner byline, follower/following rows, creator cards). "Everything is a shared
 * component": this is the single place the two controls live.
 *
 * TWO INDEPENDENT CONTROLS (Gordon, 2026-07-20) — never combined, never one
 * implying the other:
 *   • Follow  — toggles the follow graph. Label Follow ⇄ Following.
 *   • Message — get-or-creates a 1:1 DM and routes to /messages/{id}.
 * Each has its own handler and its own backend; clicking one fires ONLY its own
 * endpoint (see UserActions.test — independence assertions).
 *
 * Variants:
 *   • full    — two side-by-side ICON buttons (line-art icons) each with a hover
 *               tooltip (rows/cards with room, ≥44px touch target). The Follow
 *               icon swaps to a "following" glyph; Message is a chat-bubble icon.
 *   • compact — an overflow (⋮) menu listing BOTH actions as separate items
 *               (tiny bylines where two buttons won't fit).
 *
 * Gating: renders nothing unless USER_ACTIONS_ENABLED, and never on your own row
 * (isSelf). The Message control is ADDITIONALLY gated on CHAT_ENABLED — it has no
 * destination while the DM surface is unregistered — so USER_ACTIONS on + CHAT
 * off shows Follow only. Guests are login-gated by the two handlers (the existing
 * non-authenticated modal), so the affordance is still visible to them (Gordon:
 * show the affordance, prompt sign-in on use).
 *
 * gordon-0913-gallery-theme-auto-contrast-and-follow-icons (B): split into a
 * pure presentational `UserActionsView` (this file's other export) and this
 * hook-connected default export, so the Gallery Theme Studio's live preview
 * can render the EXACT SAME markup/icons/borderless-button styling the real
 * gallery uses — Gordon's ruling that the preview must never be a hand-drawn
 * mockup that can drift from the live header — WITHOUT pulling the live
 * redux/react-query data stack (useUserRelationship) into a design-preview
 * surface that has no real userId/backend to relate to.
 */
import React, { useState } from "react";
import PropTypes from "prop-types";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import BrandWheelLoader from "../BrandWheelLoader";
import MoreVert from "@mui/icons-material/MoreVert";
import PersonAddAltOutlined from "@mui/icons-material/PersonAddAltOutlined";
import HowToRegOutlined from "@mui/icons-material/HowToRegOutlined";
import ChatBubbleOutlineOutlined from "@mui/icons-material/ChatBubbleOutlineOutlined";
import { USER_ACTIONS_ENABLED, CHAT_ENABLED } from "../../constants/featureFlags";
import useUserRelationship from "../../hooks/useUserRelationship";
import * as Styled from "./styles";

/**
 * useUserActionsMenuItems — Follow + Message as plain menu-item DATA (no
 * wrapping <Menu>/trigger of their own), so a HOST menu can splice them in as
 * its own FIRST items instead of nesting a second menu-inside-a-menu.
 *
 * gordon-0921-discover-trending-card-heart-follow-message-kebab: the Discover
 * Trending card used to mount the full two-icon-button UserActions beside the
 * creator name (2026-08-26 ruling); Gordon reversed that 2026-09-21 — "Follow
 * and Message should be IN the Kebab" — so the SAME Follow/Message state +
 * handlers this file already owns (useUserRelationship) now needs to render as
 * menu items inside the wheel-options kebab (WheelCardThreeDotMenu's own
 * `leadingMenuItems` prop) instead of as standalone icon buttons. Reuses the
 * hook, not a fork: identical gating (USER_ACTIONS_ENABLED, isSelf, userId),
 * identical anon/login-gated handlers, identical Message-needs-a-follow-first
 * state (conveyed via the item's own label, since a menu item has no hover
 * tooltip of its own the way the full-variant IconButton did).
 */
export function useUserActionsMenuItems({
  userId,
  userLabel = "",
  initialIsFollowing,
  onFollowChange,
  showMessage = CHAT_ENABLED,
}) {
  const {
    isSelf,
    isFollowing,
    canMessage,
    followLoading,
    messageLoading,
    toggleFollow,
    openMessage,
  } = useUserRelationship(userId, {
    initialIsFollowing,
    onFollowChange,
    fetchStatus: true,
  });

  if (!USER_ACTIONS_ENABLED || isSelf || userId == null) return [];

  const forName = userLabel ? ` ${userLabel}` : "";
  const messageBlocked = !canMessage;

  const items = [
    {
      key: "follow",
      testId: "ua-follow",
      label: isFollowing ? "Following" : "Follow",
      ariaLabel: isFollowing ? `Unfollow${forName}` : `Follow${forName}`,
      icon: followLoading ? (
        <BrandWheelLoader variant="inline" fill="mono" size={16} tone="light" label="Loading" />
      ) : isFollowing ? (
        <HowToRegOutlined fontSize="small" />
      ) : (
        <PersonAddAltOutlined fontSize="small" />
      ),
      action: toggleFollow,
      isDisabled: followLoading,
    },
  ];

  if (showMessage) {
    items.push({
      key: "message",
      testId: "ua-message",
      label: messageBlocked ? "Message (follow to message)" : "Message",
      ariaLabel: messageBlocked
        ? `Message${forName} (follow them first)`
        : `Message${forName}`,
      icon: messageLoading ? (
        <BrandWheelLoader variant="inline" fill="mono" size={16} tone="light" label="Loading" />
      ) : (
        <ChatBubbleOutlineOutlined fontSize="small" />
      ),
      action: openMessage,
      isDisabled: messageLoading || messageBlocked,
    });
  }

  return items;
}

// ≥44px WCAG touch target for the full-variant icon buttons (rows/cards with room).
const ICON_BTN_SX = { width: 44, height: 44 };

// BUG-chat-search-tooltip-zindex (Kelly QA-146): the header user-search dropdown
// panel is z-index:10000 (SearchBar/styles.js), and MUI's Tooltip Popper defaults
// to theme.zIndex.tooltip = 1500 — so the Follow/Message hover tooltips rendered
// BEHIND the names box. UserActions is a shared affordance dropped into that
// dropdown (and other stacked surfaces), so its tooltip Popper must sit above the
// tallest surface it can appear over. An inline `style` on PopperProps overrides
// the class-based default and is the value the regression test reads (jsdom sees
// inline styles, not stylesheet classes). Exported for the test to pin the floor.
export const SEARCH_ACTIONS_TOOLTIP_ZINDEX = 10001;
const TOOLTIP_POPPER_PROPS = {
  style: { zIndex: SEARCH_ACTIONS_TOOLTIP_ZINDEX },
};

/**
 * UserActionsView — pure presentational Follow+Message markup. Self-manages
 * the compact variant's own menu-open state (no external control needed);
 * `onMenuOpenChange` just notifies a caller when it flips, so the hook-
 * connected UserActions below can defer its follow-status fetch until the
 * menu opens. Everything about follow/message STATE and ACTIONS is passed in
 * as props.
 */
export function UserActionsView({
  userLabel = "",
  variant = "full",
  showFollow = true,
  showMessage = CHAT_ENABLED,
  isFollowing = false,
  followLoading = false,
  messageLoading = false,
  canMessage = true,
  onFollow = () => {},
  onMessage = () => {},
  className,
  style,
  followingTooltipTitle = "Following",
  tooltipPlacement = "bottom",
  // gordon-0923-P1 (supersedes ISP-9999 #965's 8px default): the two 44px
  // icon buttons' hover-circle boxes must touch, 0px between their edges,
  // on every surface — Gordon's repeat complaint that the old 8px gap was
  // "WAY TOO BIG". A caller may still opt into more room explicitly.
  iconGap = 0,
  stack = false,
  // gordon-0913-gallery-theme-auto-contrast-and-follow-icons: optional theme
  // tint. followingIconColor overrides the Follow icon's brand-purple
  // "following" state; iconColor is the base tint for the Message icon and
  // the NOT-following Follow icon (both otherwise MUI's plain default ink).
  // Both undefined everywhere except the themed Gallery header/its Studio
  // preview, so every existing call site stays byte-identical.
  followingIconColor,
  iconColor,
  // Compact-variant menu-open notifier: this view self-manages its own
  // anchorEl/open state (uncontrolled), but a hook-connected caller (the
  // default UserActions export below) wants to know the instant it opens so
  // it can defer its follow-status fetch until then — otherwise a dense grid
  // of cards (Explore) would fire one status GET per card on mount. No-op by
  // default so every other caller is unaffected.
  onMenuOpenChange = () => {},
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const messageBlocked = !canMessage;

  if (!showFollow && !showMessage) return null;

  const forName = userLabel ? ` ${userLabel}` : "";

  // Two disjoint handlers. stopPropagation keeps a click inside a clickable row
  // (search result / card) from also navigating to the profile.
  const handleFollow = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    onFollow();
  };
  const handleMessage = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    onMessage();
  };

  if (variant === "compact") {
    const openMenu = (e) => {
      e.stopPropagation();
      setAnchorEl(e.currentTarget);
      onMenuOpenChange(true);
    };
    const closeMenu = (e) => {
      if (e && e.stopPropagation) e.stopPropagation();
      setAnchorEl(null);
      onMenuOpenChange(false);
    };
    return (
      <span
        className={className}
        style={style}
        data-testid="user-actions"
        data-variant="compact"
      >
        <IconButton
          size="small"
          sx={ICON_BTN_SX}
          aria-label={`Actions for${forName || " user"}`}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={openMenu}
          data-testid="ua-overflow"
        >
          <MoreVert fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={closeMenu}
          onClick={(e) => e.stopPropagation()}
          MenuListProps={{ dense: true }}
        >
          {showFollow && (
            <MenuItem
              onClick={(e) => {
                handleFollow(e);
                closeMenu(e);
              }}
              disabled={followLoading}
              data-testid="ua-follow"
            >
              <ListItemIcon>
                {followLoading ? (
                  <BrandWheelLoader variant="inline" fill="mono" size={16} tone="light" label="Loading" />
                ) : isFollowing ? (
                  <HowToRegOutlined fontSize="small" />
                ) : (
                  <PersonAddAltOutlined fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText>{isFollowing ? "Following" : "Follow"}</ListItemText>
            </MenuItem>
          )}
          {showMessage && (
            <MenuItem
              onClick={(e) => {
                handleMessage(e);
                closeMenu(e);
              }}
              disabled={messageLoading}
              data-testid="ua-message"
            >
              <ListItemIcon>
                {messageLoading ? (
                  <BrandWheelLoader variant="inline" fill="mono" size={16} tone="light" label="Loading" />
                ) : (
                  <ChatBubbleOutlineOutlined fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText>Message</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </span>
    );
  }

  return (
    <Styled.Row
      className={className}
      style={style}
      $gap={iconGap}
      $stack={stack}
      data-testid="user-actions"
      data-variant="full"
    >
      {showFollow && (
        <Tooltip
          title={isFollowing ? followingTooltipTitle : "Follow"}
          arrow
          placement={tooltipPlacement}
          PopperProps={TOOLTIP_POPPER_PROPS}
        >
          {/* span wrapper keeps the tip working while disabled (loading) */}
          <span style={{ display: "inline-flex" }}>
            <IconButton
              onClick={handleFollow}
              disabled={followLoading}
              // Icon alone conveys state (Gordon 2026-08-19, no text label):
              // FOLLOWING = brand-purple person-check (emphasized on-brand
              // state); NOT following = neutral person-add. Distinct in BOTH
              // color and glyph.
              color={isFollowing ? "primary" : "default"}
              sx={{
                ...ICON_BTN_SX,
                ...(isFollowing
                  ? followingIconColor && { color: followingIconColor }
                  : iconColor && { color: iconColor }),
              }}
              aria-label={isFollowing ? `Unfollow${forName}` : `Follow${forName}`}
              data-testid="ua-follow"
            >
              {followLoading ? (
                <BrandWheelLoader variant="inline" fill="mono" size={18} tone="light" label="Loading" />
              ) : isFollowing ? (
                <HowToRegOutlined fontSize="small" />
              ) : (
                <PersonAddAltOutlined fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}
      {showMessage &&
        (messageBlocked ? (
          <Tooltip
            title="Follow to Message"
            arrow
            placement={tooltipPlacement}
            PopperProps={TOOLTIP_POPPER_PROPS}
          >
            {/* span wrapper: a disabled button emits no hover events for the tip */}
            <span style={{ display: "inline-flex" }} data-testid="ua-message-wrapper">
              <IconButton
                onClick={handleMessage}
                disabled
                sx={ICON_BTN_SX}
                aria-label={`Message${forName} (follow them first)`}
                data-testid="ua-message"
              >
                <ChatBubbleOutlineOutlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Tooltip
            title="Message"
            arrow
            placement={tooltipPlacement}
            PopperProps={TOOLTIP_POPPER_PROPS}
          >
            <span style={{ display: "inline-flex" }}>
              <IconButton
                onClick={handleMessage}
                disabled={messageLoading}
                sx={{ ...ICON_BTN_SX, ...(iconColor && { color: iconColor }) }}
                aria-label={`Message${forName}`}
                data-testid="ua-message"
              >
                {messageLoading ? (
                  <BrandWheelLoader variant="inline" fill="mono" size={18} tone="light" label="Loading" />
                ) : (
                  <ChatBubbleOutlineOutlined fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        ))}
    </Styled.Row>
  );
}

UserActionsView.propTypes = {
  userLabel: PropTypes.string,
  variant: PropTypes.oneOf(["full", "compact"]),
  showFollow: PropTypes.bool,
  showMessage: PropTypes.bool,
  isFollowing: PropTypes.bool,
  followLoading: PropTypes.bool,
  messageLoading: PropTypes.bool,
  canMessage: PropTypes.bool,
  onFollow: PropTypes.func,
  onMessage: PropTypes.func,
  className: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  style: PropTypes.object,
  followingTooltipTitle: PropTypes.string,
  tooltipPlacement: PropTypes.string,
  iconGap: PropTypes.number,
  stack: PropTypes.bool,
  followingIconColor: PropTypes.string,
  iconColor: PropTypes.string,
  onMenuOpenChange: PropTypes.func,
};

export default function UserActions({
  userId,
  userLabel = "",
  initialIsFollowing,
  variant = "full",
  showFollow = true,
  showMessage = CHAT_ENABLED,
  onFollowChange,
  className,
  style,
  // BUG #933 (extra-following-pill): opt-in override for the full-variant Follow
  // icon's hover tooltip when it is already following. Callers that render their
  // OWN persistent "Following" status pill next to this icon (e.g. the Discover
  // wheel card's by-name chip) pass "Unfollow" here so hovering the icon doesn't
  // pop a second "Following" label beside the one the pill already shows — the
  // icon's tooltip then names the ACTION (Unfollow) instead of restating the
  // STATUS. Default "Following" preserves every other call site (bug #914,
  // UserActions.tooltipCopy.b914.test.jsx) byte-for-byte.
  followingTooltipTitle = "Following",
  // #965: the header global-search dropdown is a short, cramped panel where a
  // bottom-placed tooltip on the last visible row spills past the panel edge.
  // Callers in that context pass "top"; every other surface keeps the MUI
  // default ("bottom") byte-identical.
  tooltipPlacement = "bottom",
  // gordon-0923-P1 (supersedes ISP-9999 #965's 8px default): gap between the
  // Follow and Message icon buttons — the two hover-circle boxes must touch,
  // 0px between their edges, everywhere the pair renders.
  iconGap = 0,
  // QA-1014: full-variant only — stack the two controls vertically instead of a
  // row (notification follow rows want Follow above Message). Default false keeps
  // every existing caller a horizontal row, byte-identical.
  stack = false,
  // gordon-0913-gallery-theme-auto-contrast-and-follow-icons (A): optional
  // theme tint, forwarded straight through to UserActionsView. Undefined
  // everywhere except the themed Gallery header, so every other call site
  // keeps today's hardcoded brand-purple / default ink.
  followingIconColor,
  iconColor,
}) {
  // In the compact (overflow-menu) variant, defer the follow-status fetch
  // until the menu is actually opened — otherwise a dense grid of cards
  // (Explore) would fire one status GET per card on mount. Full variants
  // fetch on mount. UserActionsView owns the menu's own open/close state;
  // this just observes it via onMenuOpenChange.
  const [menuOpen, setMenuOpen] = useState(false);

  const {
    isSelf,
    isFollowing,
    canMessage,
    followLoading,
    messageLoading,
    toggleFollow,
    openMessage,
  } = useUserRelationship(userId, {
    initialIsFollowing,
    onFollowChange,
    fetchStatus: variant !== "compact" || menuOpen,
  });

  // Flag gate + never render on your own row. Each wiring site ALSO guards on
  // USER_ACTIONS_ENABLED so a flag-OFF tree never mounts this; the internal
  // guard is defence-in-depth.
  if (!USER_ACTIONS_ENABLED || isSelf || userId == null) return null;

  return (
    <UserActionsView
      userLabel={userLabel}
      variant={variant}
      showFollow={showFollow}
      showMessage={showMessage}
      isFollowing={isFollowing}
      followLoading={followLoading}
      messageLoading={messageLoading}
      canMessage={canMessage}
      onFollow={toggleFollow}
      onMessage={openMessage}
      className={className}
      style={style}
      followingTooltipTitle={followingTooltipTitle}
      tooltipPlacement={tooltipPlacement}
      iconGap={iconGap}
      stack={stack}
      followingIconColor={followingIconColor}
      iconColor={iconColor}
      onMenuOpenChange={setMenuOpen}
    />
  );
}

UserActions.propTypes = {
  /** Target user's id (the person these actions act on). */
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Display name/username, used only for aria-labels. */
  userLabel: PropTypes.string,
  /** Seed follow-state to skip the follow-status fetch when the parent knows it. */
  initialIsFollowing: PropTypes.bool,
  variant: PropTypes.oneOf(["full", "compact"]),
  showFollow: PropTypes.bool,
  /** Defaults to CHAT_ENABLED — Message has no destination without the DM surface. */
  showMessage: PropTypes.bool,
  /** Called with the new follow boolean after a successful toggle. */
  onFollowChange: PropTypes.func,
  /** Theme accent ink for the "following" icon tint (Gallery header only). */
  followingIconColor: PropTypes.string,
  /** Base tint for the Message icon and the NOT-following Follow icon (Gallery header only). */
  iconColor: PropTypes.string,
  /** Full-variant Follow icon's hover-tooltip text while following (default "Following"). */
  followingTooltipTitle: PropTypes.string,
  /** MUI Tooltip placement for the full-variant Follow/Message tooltips (default "bottom"). */
  tooltipPlacement: PropTypes.string,
  /** Gap (px) between the full-variant Follow/Message icon buttons (default 0). */
  iconGap: PropTypes.number,
  className: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  style: PropTypes.object,
};
